import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ActiveUsersTable, InactiveUsersTable, DisabledUsersTable } from '../../components/UsersTable';  // Import DisabledUsersTable
import Container from '../../layouts/Container';
import { getUsers, activate, deactivate, deleteUser } from '../../services/project/userService';
import { getRoles } from '../../services/project/roleService';
import { getRegionsWithAreas } from '../../services/project/regionService';
import { Tabs, Tab, Button, Spinner } from 'react-bootstrap';  // Import Tabs and Tab from react-bootstrap
import { downloadUsers } from '../../services/project/reportService';
import { useProjects } from '../../context/ProjectContext';

function ActivateUser() {
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [disabledUsers, setDisabledUsers] = useState([]);  // State for Disabled Users
  const [regions, setRegions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { projectId } = useParams();
  const { projects, isProjectReadOnly } = useProjects();

  useEffect(() => {
    fetchUsers();
    fetchRegions();
    fetchRoles(projectId);
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const active = await getUsers(projectId, true, false);
      if (!active.success) {
        toast.error(active.data?.errorMessage || 'Error fetching active users.');
        return;
      }
      const filteredActiveUsers = active.users.filter(user => user.role !== 'Admin');
      setActiveUsers(filteredActiveUsers);

      const inactive = await getUsers(projectId, false);
      if (!inactive.success) {
        toast.error(inactive.data?.errorMessage || 'Error fetching inactive users.');
        return;
      }
      const filteredInactiveUsers = inactive.users.filter(user => user.role !== 'Admin');
      setInactiveUsers(filteredInactiveUsers);

      const disabled = await getUsers(projectId, false, true);
      if (!disabled.success) {
        toast.error(disabled.data?.errorMessage || 'Error fetching disabled users.');
        return;
      }
      const filteredDisabledUsers = disabled.users.filter(user => user.role !== 'Admin');
      setDisabledUsers(filteredDisabledUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const { regions } = await getRegionsWithAreas(projectId);
      setRegions(regions);
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const fetchRoles = async (projectId) => {
    try {
      const { roles } = await getRoles(projectId);
      const filteredRoles = roles.filter(role => role.name !== 'Admin');
      setRoles(filteredRoles);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const handleApprove = async (user) => {
    try {
      const response = await activate(user.id, projectId);
      setInactiveUsers(prev => {
        const currentUser = prev.find(u => u.id === user.id);
        if (!currentUser) return prev;

        setActiveUsers(active => [...active, { ...currentUser, status: 'active' }]);
        return prev.filter(u => u.id !== user.id);
      });
      toast.success(response.message);
    } catch (error) {
      console.error('Error setting status:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const handleActivate = async (user) => {
    try {
      const response = await activate(user.id, projectId);
      setDisabledUsers(prev => {
        const currentUser = prev.find(u => u.id === user.id);
        if (!currentUser) return prev;

        setActiveUsers(active => [...active, { ...currentUser, status: 'active' }]);
        return prev.filter(u => u.id !== user.id);
      });
      toast.success(response.message);
    } catch (error) {
      console.error('Error setting status:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const handleDeactivate = async (user) => {
    const confirmed = window.confirm(`Deactivate ${user.username}?`);
    if (!confirmed) return;
    try {
      const response = await deactivate(user.id, projectId);
      setActiveUsers(prev => {
        const currentUser = prev.find(u => u.id === user.id);
        if (!currentUser) return prev;

        setDisabledUsers(active => [...active, { ...currentUser, status: 'active' }]);
        return prev.filter(u => u.id !== user.id);
      });
      toast.success(response.message);
    } catch (error) {
      console.error('Error setting status:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(`Delete ${user.username}?`);
    if (!confirmed) return;
    try {
      const response = await deleteUser(user.id, projectId);
      setInactiveUsers(prev => {
        const currentUser = prev.find(u => u.id === user.id);
        if (!currentUser) return prev;
        return prev.filter(u => u.id !== user.id);
      });
      setActiveUsers(prev => {
        const currentUser = prev.find(u => u.id === user.id);
        if (!currentUser) return prev;
        return prev.filter(u => u.id !== user.id);
      });
      setDisabledUsers(prev => {
        const currentUser = prev.find(u => u.id === user.id);
        if (!currentUser) return prev;
        return prev.filter(u => u.id !== user.id);
      });
      toast.success(response.message);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };


  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const project = projects.find(p => p.id === parseInt(projectId, 10));
      const firstWordLower = (project?.name || 'report').split(' ')[0].toLowerCase();
      await downloadUsers(
        {
          projectId,
        },
        `users_${new Date().toLocaleString()}.csv`
      );
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Container>
      
      <div className="d-flex align-items-center mb-3 gap-2">
        <h2>User List</h2>
        <Button variant="success" disabled={isDownloading} onClick={handleDownload}>
          {isDownloading ? <Spinner size="sm" /> : 'Export'}
        </Button>
      </div>
      {loading ? <p>Loading...</p> : (
        <Tabs defaultActiveKey="active" id="user-tabs" className="mb-3">
          {/* Active Users Tab */}
          <Tab eventKey="active" title="Active">
            <ActiveUsersTable readOnly={isProjectReadOnly} users={activeUsers} handleDeactivate={handleDeactivate} handleDelete={handleDelete} refetchUsers={fetchUsers} roles={roles} regions={regions} />
          </Tab>

          {/* Inactive Users Tab */}
          <Tab eventKey="inactive" title="Pending">
            <InactiveUsersTable readOnly={isProjectReadOnly} users={inactiveUsers} handleApprove={handleApprove} handleDelete={handleDelete} refetchUsers={fetchUsers} roles={roles} regions={regions} />
          </Tab>

          {/* Disabled Users Tab */}
          <Tab eventKey="disabled" title="Deactivated">
            <DisabledUsersTable readOnly={isProjectReadOnly} users={disabledUsers} handleActivate={handleActivate} handleDelete={handleDelete} refetchUsers={fetchUsers} roles={roles} regions={regions} />
          </Tab>
        </Tabs>
      )}
    </Container>
  );
}

export default ActivateUser;
